import { useEffect, useState } from "react";
import intl from "react-intl-universal";
import { Link, useHistory } from "react-router-dom";
import { TbArrowLeft, TbWallet } from "react-icons/tb";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { fetchBaasStablesTransactions } from "../../../../Services/baasStablesApi";
import { fetchBaasStablesWalletsList } from "../../../../Services/baasStablesWalletApi";
import { BaasStablesMetricsPageSkeleton } from "../components/BaasSkeleton";
import { BAAS_STABLES_WALLET_PATH } from "../constants";
import { BaasStablesMetricsPanel } from "./components/BaasStablesMetricsPanel";

const getBlockMessage = (blockReason) => {
  if (blockReason === "product_disabled") {
    return intl.get("baas.home.capabilities.productDisabled");
  }
  if (blockReason === "permission_denied") {
    return intl.get("baas.home.capabilities.permissionDenied");
  }
  return intl.get("baas.stablesWallet.capabilityBlocked");
};

export const BaasStablesMetricsPage = () => {
  const history = useHistory();
  const { selectedInstance } = useBaasInstance();
  const isLoading = useBaasPageLoading(selectedInstance?.id);
  const { can, getBlockReason } = useBaasCapabilities();

  const capabilityId = "processing.stables.view";
  const allowed = can(capabilityId);

  const [wallets, setWallets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!selectedInstance) {
        setWallets([]);
        setTransactions([]);
        setLoadError(false);
        return;
      }

      setDataLoading(true);
      setLoadError(false);
      try {
        const [nextWallets, nextTx] = await Promise.all([
          fetchBaasStablesWalletsList(selectedInstance),
          fetchBaasStablesTransactions(selectedInstance).catch(() => []),
        ]);
        if (!cancelled) {
          setWallets(nextWallets);
          setTransactions(nextTx);
          setLoadError(false);
        }
      } catch {
        if (!cancelled) {
          setWallets([]);
          setTransactions([]);
          setLoadError(true);
        }
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedInstance, reloadToken]);

  if (isLoading || dataLoading) {
    return <BaasStablesMetricsPageSkeleton />;
  }

  const pageHeader = (
    <header className="min-w-0 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <button
          type="button"
          className="mb-2 inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 transition hover:text-brand-dark dark:text-gray-400 dark:hover:text-gray-200"
          onClick={() => history.push(BAAS_STABLES_WALLET_PATH)}
        >
          <TbArrowLeft className="h-3.5 w-3.5" aria-hidden />
          {intl.get("baas.stablesWallet.metrics.backToWallets")}
        </button>
        <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
          {intl.get("baas.stablesWallet.metrics.pageTitle")}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.stablesWallet.metrics.pageSubtitle")}
        </p>
      </div>
      {allowed ? (
        <Link
          to={BAAS_STABLES_WALLET_PATH}
          className="btn-outline shrink-0"
        >
          <TbWallet className="h-4 w-4" />
          {intl.get("baas.stablesWallet.actions.viewWallets")}
        </Link>
      ) : null}
    </header>
  );

  if (!allowed) {
    return (
      <div className="baas-stables-metrics min-w-0 space-y-5">
        {pageHeader}
        <div className="app-card app-empty-state">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {getBlockMessage(getBlockReason(capabilityId))}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="baas-stables-metrics min-w-0 space-y-5">
      {pageHeader}

      {loadError ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-6 py-10 text-center dark:border-brand-dark-border dark:bg-brand-dark-elevated/30">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
            {intl.get("baas.stablesWallet.metrics.loadFailed")}
          </p>
          <button
            type="button"
            className="btn-outline mt-4"
            onClick={() => setReloadToken((token) => token + 1)}
          >
            {intl.get("baas.stablesWallet.retry")}
          </button>
        </div>
      ) : (
        <BaasStablesMetricsPanel wallets={wallets} transactions={transactions} />
      )}
    </div>
  );
};

export default BaasStablesMetricsPage;
